import { z } from 'zod';
/**
 * Derivados que el worker genera al subir una imagen, y que el sitio público usa en `srcset`.
 *
 * Lista **cerrada** de proporciones (handoff del sitio público, §7): 16:9 para portada
 * posición 1, tiras y foto de nota; 4:3 para miniaturas; 3:4 solo para la foto vertical
 * dentro del cuerpo (y solo se genera si la original es vertical). No hay más proporciones
 * a propósito: cada recorte nuevo es peso en el bucket y una decisión de encuadre.
 *
 * Aparte de los tres recortes está **`full`: la imagen entera, solo redimensionada**. No es
 * una proporción, es la ausencia de recorte, y existe porque la foto del cuerpo de una nota
 * no se puede recortar (desvío 9 del handoff del sitio: la vertical va en su proporción
 * original limitada a 80 vh, nunca a panorámica). Sin `full` el cuerpo no tendría más opción
 * que bajar el original completo.
 *
 * El shape vive en contracts porque lo comparten tres lados: el worker que escribe
 * `Media.variants`, la API pública que lo serializa y el sitio que arma el `srcset`.
 *
 * ## Guardado vs. publicado
 *
 * Lo guardado lleva la **key** del objeto; lo publicado, la **URL**. Es la regla de fondo del
 * sistema: ninguna dirección de imagen se persiste, porque una URL guardada ata el contenido
 * al dominio, al bucket y al CDN del día en que se subió. La URL la arma la API en cada
 * respuesta (`urlPublicaDeKey`), así que mudar el bucket o poner un CDN adelante no pide
 * reescribir una sola fila.
 */
export const MEDIA_VARIANT_RATIOS = ['16:9', '4:3', '3:4'];
/** Los tres recortes más `full`: todo lo que puede haber en `Media.variants`. */
export const MEDIA_VARIANT_KEYS = [...MEDIA_VARIANT_RATIOS, 'full'];
/** Anchos generados por clave. El worker saltea los mayores que la imagen original. */
export const MEDIA_VARIANT_WIDTHS = {
    '16:9': [400, 800, 1200, 1600],
    '4:3': [320, 640, 960],
    '3:4': [600, 1000],
    full: [800, 1200, 1600],
};
/**
 * Encuadre de los recortes: tercio superior (`object-position: center 30%`), que es donde
 * está la cara o el hecho cuando la redacción sube una foto vertical de celular.
 */
export const MEDIA_VARIANT_FOCUS_Y = 0.3;
/** Una variante **tal como se guarda**: la key del objeto, nunca su dirección. */
export const MediaVariantStored = z
    .object({
    /** Ancho en px; junto con la clave define el archivo. */
    width: z.number().int().min(1),
    height: z.number().int().min(1),
    /** Key del objeto en el bucket (`tenants/…__16x9_800.webp`). */
    key: z.string().min(1),
})
    .strict();
/** Lo que se guarda en `Media.variants`. Toda clave es opcional. */
export const MediaVariantsStoredSchema = z
    .object({
    '16:9': z.array(MediaVariantStored),
    '4:3': z.array(MediaVariantStored),
    '3:4': z.array(MediaVariantStored),
    full: z.array(MediaVariantStored),
})
    .partial()
    .strict();
export const MediaVariant = z
    .object({
    width: z.number().int().min(1),
    height: z.number().int().min(1),
    /** Dirección pública directa; `null` si el bucket no tiene base pública. */
    url: z.string().nullable(),
})
    .strict();
/**
 * Lo que viaja al sitio, con las URLs ya resueltas. Las claves son opcionales: `3:4` solo
 * existe para originales verticales, y una media subida antes del worker no tiene ninguna.
 */
export const MediaVariantsSchema = z
    .object({
    '16:9': z.array(MediaVariant),
    '4:3': z.array(MediaVariant),
    '3:4': z.array(MediaVariant),
    full: z.array(MediaVariant),
})
    .partial()
    .strict();
const RATIO_VALUE = {
    '16:9': 16 / 9,
    '4:3': 4 / 3,
    '3:4': 3 / 4,
};
/**
 * Caja de recorte dentro de la imagen original: la más grande que entra con esa proporción,
 * centrada horizontal y con el excedente vertical repartido para que el recorte caiga en el
 * tercio superior (`MEDIA_VARIANT_FOCUS_Y`). Pura para poder testearla sin sharp.
 */
export function cropBox(original, ratio) {
    if (original.width <= 0 || original.height <= 0) {
        throw new Error(`dimensiones inválidas: ${original.width}×${original.height}`);
    }
    const objetivo = RATIO_VALUE[ratio];
    const propia = original.width / original.height;
    let width = original.width;
    let height = original.height;
    if (propia > objetivo) {
        // Más ancha que el objetivo: se recorta a los costados, centrado.
        width = Math.round(original.height * objetivo);
    }
    else {
        // Más alta que el objetivo: se recorta arriba/abajo, sesgado al tercio superior.
        height = Math.round(original.width / objetivo);
    }
    const left = Math.round((original.width - width) / 2);
    const top = Math.round((original.height - height) * MEDIA_VARIANT_FOCUS_Y);
    return { left, top, width, height };
}
